import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  HowItWorks — 4-step rail used on home / facility / clinician pages*/
/* ------------------------------------------------------------------ */

export default function HowItWorks({
  eyebrow = "How it works",
  title = "From signup to your first shift in under 72 hours.",
  steps = [],
  cta,
  variant = "light", // "light" | "dark"
  testid = "how-it-works",
}) {
  const dark = variant === "dark";
  return (
    <section
      className={`${dark ? "bg-trital-navy-ink text-white" : "bg-white text-slate-900"} relative overflow-hidden`}
      data-testid={testid}
    >
      {dark && <div className="absolute inset-0 bg-trital-navy-mesh opacity-80" aria-hidden="true" />}
      {dark && <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />}
      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-7">
            <div className={`overline ${dark ? "text-trital-red" : "text-trital-red"}`}>{eyebrow}</div>
            <h2 className={`mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-balance leading-[1.05] ${dark ? "text-white" : "text-slate-900"}`}>
              {title}
            </h2>
          </div>
          {cta && (
            <div className="lg:col-span-5 lg:text-right">
              <Link
                to={cta.to}
                className={`inline-flex items-center gap-1.5 px-5 py-3 rounded-full text-[13.5px] font-semibold btn-lift ${
                  dark ? "bg-white text-trital-navy hover:bg-slate-100" : "bg-trital-navy text-white hover:bg-trital-navy-deep"
                }`}
                data-testid={`${testid}-cta`}
              >
                {cta.label} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              </Link>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.35, delay: i * 0.06 }}
              className={`relative rounded-3xl p-6 lg:p-7 ${
                dark
                  ? "bg-white/5 border border-white/10"
                  : "bg-white border border-slate-200 shadow-card"
              }`}
              data-testid={`${testid}-step-${i}`}
            >
              <div className={`font-display font-black text-3xl ${dark ? "text-trital-red" : "text-trital-red"}`}>{s.num}</div>
              <div className={`mt-3 font-display font-bold text-[19px] leading-tight ${dark ? "text-white" : "text-slate-900"}`}>{s.title}</div>
              <p className={`mt-2.5 text-[13.5px] leading-relaxed ${dark ? "text-white/70" : "text-slate-600"}`}>{s.blurb}</p>
              {/* subtle connector line on lg+ */}
              {i < steps.length - 1 && (
                <div className={`hidden lg:block absolute top-1/2 -right-3 h-px w-6 ${dark ? "bg-white/15" : "bg-slate-200"}`} />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
